// Agency timezone helpers.
//
// Event, timeslot and hold times are stored as UTC timestamps. They are
// displayed and entered in the agency's timezone (RTDB `agencies/{id}/timezone`,
// an IANA name like "America/Los_Angeles"), falling back to the browser's
// timezone when the agency has none set.

import { useEffect } from 'react';
import moment from 'moment-timezone';

export function agencyTz(agency) {
  return (agency && agency.timezone) || moment.tz.guess();
}

// A moment for `value` in timezone `tz`. Like `moment(value)`, an undefined
// value means now.
export function inTz(value, tz) {
  return moment(value).tz(tz || moment.tz.guess());
}

// Material-UI date/time pickers read and write browser-local Dates. This
// returns a Date whose local wall-clock matches `value` in `tz`, so the picker
// shows agency time. Read picked values back with `fromPickerDate`, or with
// `moment(date)` (local) when applying only some fields to an `inTz` moment.
export function toPickerDate(value, tz) {
  const m = inTz(value, tz);
  return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second());
}

// The inverse of `toPickerDate`: reads the local wall-clock of a picked Date
// as a time in `tz`.
export function fromPickerDate(date, tz) {
  const d = new Date(date);
  return moment.tz([d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()], tz || moment.tz.guess());
}


// Workaround for the skybolt-ui / skybolt-api packages.
//
// A few package internals use bare `moment(...)`, which works in the browser's
// timezone rather than the agency's:
// - skybolt-ui InputDate parses typed/picked dates as browser-local midnight.
// - skybolt-api updateInvoice re-stamps line item times and computes hours.
//
// Rather than patch the packages, hold moment's default timezone at the
// agency's while that code runs. This affects every bare `moment(...)` in the
// app for as long as it's held, so keep holds short and scoped to billing.
// Holds are counted; the default resets to browser time on the last release.

let defaultTzHolds = 0;

export function holdDefaultTz(tz) {
  if(!tz) {
    return () => null;
  }
  defaultTzHolds += 1;
  moment.tz.setDefault(tz);

  let released = false;
  return () => {
    if(released) {
      return;
    }
    released = true;
    defaultTzHolds -= 1;
    if(defaultTzHolds === 0) {
      moment.tz.setDefault();
    }
  };
}

// Hold the default timezone while a component is mounted.
export function useDefaultTz(tz) {
  useEffect(() => holdDefaultTz(tz), [tz]);
}

// Run an async function with the default timezone held.
export async function withDefaultTz(tz, fn) {
  const release = holdDefaultTz(tz);
  try {
    return await fn();
  }
  finally {
    release();
  }
}
